/**
 * Copyright IBM Corp. 2022, 2026
 */

import { FlightIcon } from '@hashicorp/mds-react/components'
import Image from 'components/optimized-image'

import { PersonRecord } from 'types/dato'

export default function SpeakersSection({ title, speakers }) {
	return (
		<section className="c-speakers-section">
			<h3 className="g-type-display-5 speakers-header">
				{title || `Speaker${speakers.length > 1 ? 's' : ''}`}
			</h3>
			<ul className="speakers">
				{speakers.map((speaker) => (
					<li
						className={`speaker${speakers.length === 1 ? ' single' : ''}`}
						key={speaker.name}
					>
						<PersonCard person={speaker} />
					</li>
				))}
			</ul>
		</section>
	)
}

export interface PersonCardProps {
	person: PersonRecord
}

function PersonCard({ person }: PersonCardProps) {
	const { photo, name, company, socialProfiles, jobTitle } = person

	return (
		<>
			<div className="person-image">
				{photo ? (
					<Image {...photo} aspectRatio={null} alt={name} sizes="200w" />
				) : (
					<div className="placeholder">
						<span className="initial">{getInitials(name)}</span>
					</div>
				)}
			</div>
			<div className="person-info">
				<span className="name">{name}</span>
				<span className="g-type-body-small job">
					{jobTitle}
					{company && `, ${company.name}`}
				</span>
				<ul className="social-links">
					{socialProfiles &&
						socialProfiles.map(({ url, networkName }) => (
							<li key={url}>
								<a href={url} rel="noopener noreferrer" target="_blank">
									<FlightIcon
										title={`${networkName} icon`}
										name={networkName}
										color="#D0D2D5"
										size={24}
									/>
								</a>
							</li>
						))}
				</ul>
			</div>
		</>
	)

	// Returns the first letters of the first and last names. Returns a single letter if
	// one word is provided.
	function getInitials(name) {
		return name
			.trim()
			.split(/ +/g)
			.filter((n, i, a) => i === 0 || i === a.length - 1) // Get first and last words of name
			.reduce((acc, name) => acc + name[0], '') // Get first letters of each word
			.toUpperCase()
	}
}
